import type { Direction } from "../contract/types";
import { useConfig } from "../config/ConfigProvider";
import { useLines } from "../config/LinesProvider";
import { usePost } from "../posts/PostProvider";

/** Sélecteur de ligne, limité aux lignes du poste actif s'il y a isolation. */
export function LinePicker({
  value,
  onChange,
  withDirection,
  direction,
  onDirectionChange,
}: {
  value: string;
  onChange: (lineId: string) => void;
  withDirection?: boolean;
  direction?: Direction;
  onDirectionChange?: (d: Direction) => void;
}) {
  const lines = useLines();
  const { activeLineIds } = usePost();
  const allowed = activeLineIds ? new Set(activeLineIds) : null;
  const options = lines.lines.filter((l) => (allowed ? allowed.has(l.id) : true));

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span className="ovl">Ligne</span>
      <div className="select is-small">
        <label className="is-sr-only">Ligne</label>
        <select value={value} onChange={(e) => onChange(e.target.value)}>
          {options.map((l) => (
            <option key={l.id} value={l.id}>
              {l.code} — {l.name}
            </option>
          ))}
        </select>
      </div>
      {withDirection && onDirectionChange ? (
        <div className="buttons has-addons" style={{ margin: 0 }}>
          {([0, 1] as Direction[]).map((d) => (
            <button
              key={d}
              type="button"
              className={`button is-small ${d === direction ? "is-primary" : ""}`}
              style={d === direction ? { background: "var(--sentinelle-rail)", borderColor: "var(--sentinelle-rail)", color: "#fff" } : undefined}
              onClick={() => onDirectionChange(d)}
            >
              {d === 0 ? "Aller" : "Retour"}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Première ligne autorisée pour le poste actif — valeur de départ des vues réseau. */
export function useDefaultLineId(): string {
  const lines = useLines();
  const { activeLineIds } = usePost();
  const allowed = activeLineIds ? new Set(activeLineIds) : null;
  return lines.lines.find((l) => (allowed ? allowed.has(l.id) : true))?.id ?? lines.lines[0]?.id ?? "";
}

/**
 * Ligne de départ de l'écran Espace-temps (J.11) : `config.defaultSpacetimeLineId`
 * s'il est fourni ET connu du référentiel ET autorisé pour le poste actif ; sinon
 * on retombe sur la première ligne autorisée (`useDefaultLineId`). Rien codé en
 * dur côté frontend (CDC §1.1) — la valeur vient de la configuration réseau.
 */
export function useSpacetimeDefaultLineId(): string {
  const lines = useLines();
  const { activeLineIds } = usePost();
  const { defaultSpacetimeLineId } = useConfig();
  const fallback = useDefaultLineId();
  const allowed = activeLineIds ? new Set(activeLineIds) : null;
  if (
    defaultSpacetimeLineId &&
    lines.lines.some((l) => l.id === defaultSpacetimeLineId) &&
    (!allowed || allowed.has(defaultSpacetimeLineId))
  ) {
    return defaultSpacetimeLineId;
  }
  return fallback;
}
